import React, { useEffect, useRef, memo } from "react";
import ApexCharts from "apexcharts";
import { useSelector } from "react-redux";
const LineChart = ({
  icon,
  title,
  data,
  legend,
  categories,
  yAxisConfig,
  classTitle,
  series,
  showPercentage = true,
  colors,
  height = 350,
}) => {
  const chartRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const isPermissionCompleted = user?.isCompleted;
  //console.log(isPermissionCompleted, "isPermissionCompleted");

  useEffect(() => {
    const allValues = series
      ? series.flatMap((s) => s.data || [])
      : data
        ? data
        : [];
    const realMaxVal = Math.max(
      ...allValues.map((v) =>
        typeof v === "object" ? (v?.y ?? v?.value ?? 0) : v || 0,
      ),
      0,
    );

    let calculatedMax = yAxisConfig?.max;
    let calculatedTickAmount = yAxisConfig?.tickAmount;
    if (calculatedMax === undefined) {
      if (realMaxVal > 0) {
        const valWithHeadroom = realMaxVal * 1.15;
        if (realMaxVal >= 100) {
          calculatedMax = Math.ceil(valWithHeadroom / 100) * 100;
        } else if (realMaxVal >= 10) {
          calculatedMax = Math.ceil(valWithHeadroom / 10) * 10;
        } else if (realMaxVal >= 1) {
          calculatedMax = Math.ceil(valWithHeadroom);
        } else {
          // Dynamic calculation for values < 1 to get nice clean round fractions
          const exponent = Math.floor(Math.log10(valWithHeadroom));
          const magnitude = Math.pow(10, exponent);
          const normalized = valWithHeadroom / magnitude;
          const roundedNormalized = Math.ceil(normalized * 2) / 2;
          calculatedMax = Number(
            (roundedNormalized * magnitude).toFixed(-exponent + 1),
          );

          calculatedTickAmount =
            roundedNormalized % 1 === 0
              ? roundedNormalized
              : roundedNormalized * 2;
          if (calculatedTickAmount > 6 && calculatedTickAmount % 2 === 0) {
            calculatedTickAmount = calculatedTickAmount / 2;
          }
        }
      } else {
        calculatedMax = 10;
      }
    }

    const options = {
      chart: {
        type: "line",
        toolbar: { show: false },
        height: height,
        zoom: { enabled: false },
      },
      series: series || [{ name: legend, data }],
      xaxis: {
        categories: categories,
        title: {
          text: "Month",
          style: { fontSize: "11px", color: "#000", fontWeight: "normal" },
        },
        labels: {
          style: { fontSize: "11px", color: "#000", fontWeight: "normal" },
        },
        axisBorder: { show: true, color: "#000" },
      },
      yaxis: {
        title: {
          text: yAxisConfig?.label || "In MtCO₂e",
          style: { fontSize: "11px", color: "#000", fontWeight: "normal" },
        },
        min: yAxisConfig?.min !== undefined ? yAxisConfig.min : 0,
        max: calculatedMax,
        tickAmount: calculatedTickAmount,
        labels: {
          style: { fontSize: "11px", color: "#000", fontWeight: "normal" },
          formatter: (val) => {
            if (showPercentage) return `${val}%`;
            if (val === 0) return "0";
            if (val % 1 !== 0) {
              return parseFloat(val.toFixed(4));
            }
            return val;
          },
        },
        axisBorder: { show: true, color: "#000" },
      },
      grid: {
        borderColor: "#06513D",
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: false } },
      },
      colors: colors || ["#458276", "#6AA4F7", "#E8C458", "#EAA89D", "#52ADC1"],
      stroke: { width: 2 },
      markers: {
        size: 4,
        colors: colors || [
          "#458276",
          "#6AA4F7",
          "#E8C458",
          "#EAA89D",
          "#52ADC1",
        ],
        strokeColors: "#fff",
        strokeWidth: 2,
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "11px",
        labels: { colors: "#000" },
        markers: {
          shape: "square",
          radius: 2,
        },
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300,
            },
            xaxis: {
              labels: {
                style: { fontSize: "10px" },
              },
            },
            yaxis: {
              labels: {
                style: { fontSize: "10px" },
              },
            },
            legend: {
              fontSize: "10px",
              position: "bottom",
            },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 260,
            },
            xaxis: {
              labels: {
                rotate: -45,
                style: { fontSize: "9px" },
              },
            },
            yaxis: {
              labels: {
                style: { fontSize: "9px" },
              },
            },
            legend: {
              fontSize: "9px",
              position: "bottom",
            },
          },
        },
      ],
    };

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();

    return () => chart.destroy();
  }, [data, legend, categories, yAxisConfig, series]);

  return (
    <div className="w-full px-4 sm:px-6 md:px-8">
      {(icon || title) && (
        <div className="flex items-center gap-2 justify-start mb-2 min-h-[48px]">
          {icon && (
            <span className="text-[#458276] text-2xl md:text-3xl flex-shrink-0">
              {icon}
            </span>
          )}
          {title && (
            <h2
              className={`font-bold capitalize text-[1rem] md:text-base text-[#06513D] ${classTitle}`}
            >
              {title}
            </h2>
          )}
        </div>
      )}

      <div
        className={` ${
          !isPermissionCompleted ? "blur-sm opacity-60 pointer-events-none" : ""
        }`}
        ref={chartRef}
      />
    </div>
  );
};

export default memo(LineChart);
